import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Package } from 'lucide-react';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { AssetSearchBar, type AssetFilters } from './AssetFilterRail';
import { AssetGallery } from './AssetGallery';
import { AssetDetailDrawer } from './AssetDetailDrawer';

const DEFAULT_FILTERS: AssetFilters = {
  assetType: null,
  sceneTag: null,
  channel: null,
  playbookId: null,
  briefId: null,
  brandCompliant: null,
  quality: 0,
  query: '',
  sortBy: 'spend',
  activeOnly: true,
};

export function AssetsView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { assetId } = useParams<{ assetId?: string }>();
  const { clientUid } = useCreativeWorkbench();

  // Default to "Creatives" tab; URL query param overrides (so "Back to Assets"
  // from the detail page can return to whichever tab was active).
  const initialTab = searchParams.get('tab') === 'assets' ? 'assets' : 'creatives';
  const [tab, setTab] = useState<'assets' | 'creatives'>(initialTab);
  const [filters, setFilters] = useState<AssetFilters>(DEFAULT_FILTERS);

  const handleOpenAsset = (id: string) => navigate(`/creative/assets/${id}`);
  const handleCloseDrawer = () => navigate('/creative/assets?tab=' + tab);

  if (!clientUid) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-10 text-center">
        <Package size={24} className="mx-auto text-slate-400 mb-3" />
        <p className="text-sm text-slate-500">
          Pick a client in the header to browse assets.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Page Header ── */}
      <div className="flex items-center gap-2">
        <Package size={18} className="text-teal-500" />
        <h1 className="text-xl font-bold text-navy-900">Assets</h1>
      </div>

      {/* ── Universal Search Bar + Filters (tab toggle embedded inside) ── */}
      <AssetSearchBar
        value={filters}
        onChange={setFilters}
        tab={tab}
        onTabChange={setTab}
      />

      {/* ── Gallery ── */}
      <AssetGallery
        clientUid={clientUid}
        filters={filters}
        mode={tab}
        onAssetClick={handleOpenAsset}
        onCreativeClick={(id) => navigate(`/creative/assets/creative/${encodeURIComponent(id)}`)}
      />

      {assetId && (
        <AssetDetailDrawer
          assetId={assetId}
          clientUid={clientUid}
          onClose={handleCloseDrawer}
        />
      )}
    </div>
  );
}
